import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import {
  EQUIPMENT_STATUS,
  type EquipmentCalibrationView,
  type EquipmentUsageView,
  type EquipmentView,
} from '@lims/shared-types';
import { equipmentApi, usersApi } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import { formatBytes, formatDateTime } from '../../components/format.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  DataTable,
  DateField,
  Dialog,
  ErrorState,
  FileDownloadButton,
  FileUploadField,
  LoadingState,
  PageHeader,
  Panel,
  ReadOnlyField,
  SelectField,
  StatusBadge,
  SwitchField,
  TextAreaField,
  TextField,
  useToast,
  type DataColumn,
} from '../../components/ui/index.js';
import { currentLocale } from '../../i18n/index.js';
import { equipmentName, formatCalendarDate } from '../work/status.js';
import { EquipmentMutationError } from './equipment-errors.js';
import { RecordStrip, StripItem } from '../reports/register-panel.js';
import { CalibrationBadge, calibrationState, useEquipmentNoticeDays } from './calibration-state.js';

/** Nothing chosen in an optional select — never a uuid, so it never reaches a body. */
const NO_CHOICE = 'none';

/**
 * The equipment file — Phase 11 (D156), completing the Phase 8 register.
 *
 * ===========================================================================
 * READ CLAUDE.md SECTION 4 BEFORE ADDING ANYTHING TO THIS PAGE.
 * ===========================================================================
 *
 * NO PASS/FAIL. Whether a calibration is acceptable is the provider's
 * statement on the certificate — a fact this page records in the
 * laboratory's own words (provider, certificate number, valid-until), never
 * one it computes or judges.
 *
 * THE HISTORY IS APPEND-ONLY. Nothing here edits or deletes a calibration
 * entry; "record a calibration" always adds a new row, and the row that sets
 * the instrument's current valid-until date is simply the one with the
 * latest effective date (`isCurrent`), never a row picked by hand.
 *
 * ATTACHING A CERTIFICATE IS NOT THE SYSTEM READING IT (CLAUDE.md section 8).
 * A "removed" certificate still downloads — the link was withdrawn, the
 * evidence kept — and the file itself is never deleted.
 *
 * Every action is offered from a permission held by the caller and
 * re-checked by the server on every request (section 5.1): editing needs
 * `equipment.manage`, recording a calibration or a certificate needs
 * `calibration.manage` (plus `files.upload` / `files.delete` for the file
 * parts), and usage history additionally needs `assignments.view`.
 */
export function EquipmentDetailPage() {
  const { t } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = useAuth();

  const mayManage = can(PERMISSIONS.EQUIPMENT_MANAGE);
  const mayCalibrate = can(PERMISSIONS.CALIBRATION_MANAGE);
  const mayReadUsage = can(PERMISSIONS.ASSIGNMENTS_VIEW);
  const noticeDays = useEquipmentNoticeDays();
  const locale = currentLocale();

  const item = useQuery({
    queryKey: ['equipment', 'detail', id],
    queryFn: () => equipmentApi.get(id),
    enabled: id !== '',
  });

  if (item.isLoading) return <LoadingState label={t('common.loading')} />;
  if (item.error || !item.data) {
    return <ErrorState error={item.error} onRetry={() => void item.refetch()} />;
  }

  const equipment = item.data;
  const calibration = calibrationState(equipment, noticeDays);
  const validUntil = equipment.calibrationValidUntil
    ? formatCalendarDate(equipment.calibrationValidUntil, locale)
    : null;

  return (
    <>
      <PageHeader
        title={equipment.equipmentCode}
        subtitle={equipmentName(equipment) || undefined}
        breadcrumbs={[
          { label: t('nav.equipment'), to: '/equipment' },
          { label: equipment.equipmentCode },
        ]}
        actions={
          <ActionButton priority="tertiary" onPress={() => navigate('/equipment')}>
            {t('common.back')}
          </ActionButton>
        }
      />

      <div className="flex flex-col gap-gutter">
        {/*
          The record bar (D278): the status, the server's usability verdict for
          today, and the calibration validity — the one most looked for, so it
          leads, coloured by how it reads against today.
        */}
        <RecordStrip>
          <StripItem label={t('equipment.calibrationColumn')}>
            <CalibrationBadge state={calibration} />
            <span className="text-caption text-default-700">
              {!equipment.calibrationRequired
                ? t('equipment.calibrationNotRequired')
                : validUntil
                  ? t('equipment.validUntil', { date: validUntil })
                  : t('equipment.noCalibrationRecorded')}
            </span>
          </StripItem>
          <StripItem label={t('equipment.usabilityColumn')}>
            <StatusBadge
              label={
                equipment.usability.usable
                  ? t('equipment.usableToday')
                  : t(`equipmentBlock.${equipment.usability.blockReason ?? 'suspended'}`)
              }
              tone={equipment.usability.usable ? 'info' : 'danger'}
            />
          </StripItem>
          <StripItem label={t('common.status')}>
            <StatusBadge
              label={t(`equipmentStatus.${equipment.status}`)}
              tone={equipment.status === EQUIPMENT_STATUS.IN_SERVICE ? 'neutral' : 'warning'}
            />
            {equipment.statusReason ? (
              <span className="text-caption text-default-600">
                <bdi>{equipment.statusReason}</bdi>
              </span>
            ) : null}
          </StripItem>
        </RecordStrip>

        {calibration === 'expired' && validUntil ? (
          <Alert
            tone="danger"
            title={t('equipment.calibrationAlert.expiredTitle', { date: validUntil })}
            description={t('equipment.calibrationAlert.expiredBody')}
          />
        ) : calibration === 'none' ? (
          <Alert
            tone="danger"
            title={t('equipment.calibrationAlert.noneTitle')}
            description={t('equipment.calibrationAlert.noneBody')}
          />
        ) : calibration === 'due_soon' && validUntil ? (
          <Alert
            tone="warning"
            title={t('equipment.calibrationAlert.dueSoonTitle', { date: validUntil })}
            description={t('equipment.calibrationAlert.dueSoonBody', { days: noticeDays ?? 0 })}
          />
        ) : null}

        <div className="grid items-start gap-gutter lg:grid-cols-[minmax(0,1fr)_22rem]">
          <div className="flex min-w-0 flex-col gap-gutter">
            <CalibrationHistoryPanel
              equipment={equipment}
              mayCalibrate={mayCalibrate}
              mayUpload={can(PERMISSIONS.FILES_UPLOAD)}
              mayRemoveCertificate={can(PERMISSIONS.FILES_DELETE)}
            />
            {mayReadUsage ? <UsageHistoryPanel equipmentId={equipment.id} /> : null}
          </div>
          <div className="flex min-w-0 flex-col gap-gutter">
            <OverviewPanel equipment={equipment} mayManage={mayManage} />
          </div>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Overview and edit
// ---------------------------------------------------------------------------

interface EditDraft {
  equipmentCode: string;
  name: string;
  equipmentType: string;
  manufacturer: string;
  model: string;
  serialNumber: string;
  locationText: string;
  responsibleUserId: string | null;
  calibrationRequired: boolean;
  notes: string;
}

function draftFrom(equipment: EquipmentView): EditDraft {
  return {
    equipmentCode: equipment.equipmentCode,
    name: equipment.name,
    equipmentType: equipment.equipmentType ?? '',
    manufacturer: equipment.manufacturer ?? '',
    model: equipment.model ?? '',
    serialNumber: equipment.serialNumber ?? '',
    locationText: equipment.locationText ?? '',
    responsibleUserId: equipment.responsibleUserId,
    calibrationRequired: equipment.calibrationRequired,
    notes: equipment.notes ?? '',
  };
}

function OverviewPanel({
  equipment,
  mayManage,
}: {
  readonly equipment: EquipmentView;
  readonly mayManage: boolean;
}) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const { show } = useToast();
  const queryClient = useQueryClient();

  const [isEditing, setEditing] = useState(false);
  const [draft, setDraft] = useState<EditDraft>(() => draftFrom(equipment));

  const mayReadUsers = can(PERMISSIONS.USERS_VIEW);
  const users = useQuery({
    queryKey: ['users', 'for-equipment-responsibility'],
    queryFn: () => usersApi.list({ includeInactive: false, pageSize: 200 }),
    enabled: mayReadUsers && isEditing,
  });

  const orNull = (value: string) => (value.trim() === '' ? null : value.trim());

  const save = useMutation({
    mutationFn: () =>
      equipmentApi.update(equipment.id, {
        equipmentCode: draft.equipmentCode.trim(),
        name: draft.name.trim(),
        equipmentType: orNull(draft.equipmentType),
        manufacturer: orNull(draft.manufacturer),
        model: orNull(draft.model),
        serialNumber: orNull(draft.serialNumber),
        locationText: orNull(draft.locationText),
        responsibleUserId: draft.responsibleUserId,
        calibrationRequired: draft.calibrationRequired,
        notes: orNull(draft.notes),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['equipment'] });
      show({ tone: 'success', message: t('equipment.saved') });
      setEditing(false);
    },
  });

  const openEdit = () => {
    setDraft(draftFrom(equipment));
    save.reset();
    setEditing(true);
  };

  const isNamed = draft.name.trim() !== '';
  const canSave = draft.equipmentCode.trim() !== '' && isNamed;

  const responsibleOptions = [
    { value: NO_CHOICE, label: t('common.none') },
    ...(users.data?.data ?? []).map((user) => ({ value: user.id, label: user.displayName })),
  ];

  return (
    <Panel
      title={t('equipment.detail.overview')}
      actions={
        mayManage ? (
          <ActionButton priority="secondary" size="sm" onPress={openEdit}>
            {t('common.edit')}
          </ActionButton>
        ) : null
      }
    >
      <div className="grid grid-cols-1 gap-stack">
        <ReadOnlyField label={t('equipment.nameLabel')}>
          <bdi>{equipment.name}</bdi>
        </ReadOnlyField>
        <ReadOnlyField label={t('equipment.detail.equipmentType')}>
          <bdi>{equipment.equipmentType ?? t('common.notRecorded')}</bdi>
        </ReadOnlyField>
        <ReadOnlyField label={t('equipment.detail.manufacturer')}>
          <bdi>{equipment.manufacturer ?? t('common.notRecorded')}</bdi>
        </ReadOnlyField>
        <ReadOnlyField label={t('equipment.detail.model')}>
          <bdi>{equipment.model ?? t('common.notRecorded')}</bdi>
        </ReadOnlyField>
        <ReadOnlyField label={t('equipment.detail.serialNumber')}>
          <span className="measure" dir="ltr">
            {equipment.serialNumber ?? t('common.notRecorded')}
          </span>
        </ReadOnlyField>
        <ReadOnlyField label={t('equipment.detail.locationText')}>
          <bdi>{equipment.locationText ?? t('common.notRecorded')}</bdi>
        </ReadOnlyField>
        <ReadOnlyField label={t('equipment.detail.responsibleUser')}>
          <bdi>{equipment.responsibleUser?.displayName ?? t('common.notRecorded')}</bdi>
        </ReadOnlyField>
        <ReadOnlyField label={t('equipment.calibrationRequiredLabel')}>
          {equipment.calibrationRequired ? t('common.yes') : t('common.no')}
        </ReadOnlyField>
        <div className="min-w-0">
          <ReadOnlyField label={t('equipment.notesLabel')}>
            <bdi className="whitespace-pre-wrap">{equipment.notes ?? t('common.notRecorded')}</bdi>
          </ReadOnlyField>
        </div>
      </div>

      <Dialog
        isOpen={isEditing}
        onClose={() => setEditing(false)}
        title={t('common.edit')}
        size="lg"
        footer={
          <ActionBar>
            <ActionButton priority="tertiary" onPress={() => setEditing(false)} isDisabled={save.isPending}>
              {t('common.cancel')}
            </ActionButton>
            <ActionButton
              priority="primary"
              isDisabled={!canSave}
              isLoading={save.isPending}
              onPress={() => save.mutate()}
            >
              {t('common.save')}
            </ActionButton>
          </ActionBar>
        }
      >
        <div className="flex flex-col gap-stack">
          <TextField
            label={t('equipment.codeLabel')}
            value={draft.equipmentCode}
            onChange={(value) => setDraft((d) => ({ ...d, equipmentCode: value }))}
            isRequired
          />
          <TextField
            label={t('equipment.nameLabel')}
            value={draft.name}
            onChange={(value) => setDraft((d) => ({ ...d, name: value }))}
            textDirection="auto"
            isRequired
          />
          <TextField
            label={t('equipment.detail.equipmentType')}
            value={draft.equipmentType}
            onChange={(value) => setDraft((d) => ({ ...d, equipmentType: value }))}
          />
          <TextField
            label={t('equipment.detail.manufacturer')}
            value={draft.manufacturer}
            onChange={(value) => setDraft((d) => ({ ...d, manufacturer: value }))}
          />
          <TextField
            label={t('equipment.detail.model')}
            value={draft.model}
            onChange={(value) => setDraft((d) => ({ ...d, model: value }))}
          />
          <TextField
            label={t('equipment.detail.serialNumber')}
            value={draft.serialNumber}
            onChange={(value) => setDraft((d) => ({ ...d, serialNumber: value }))}
          />
          <TextField
            label={t('equipment.detail.locationText')}
            value={draft.locationText}
            onChange={(value) => setDraft((d) => ({ ...d, locationText: value }))}
          />
          {mayReadUsers ? (
            <SelectField
              label={t('equipment.detail.responsibleUser')}
              description={t('equipment.detail.responsibleUserHint')}
              value={draft.responsibleUserId ?? NO_CHOICE}
              onChange={(value) =>
                setDraft((d) => ({ ...d, responsibleUserId: value === NO_CHOICE ? null : value }))
              }
              options={responsibleOptions}
            />
          ) : null}
          <SwitchField
            label={t('equipment.calibrationRequiredLabel')}
            description={t('equipment.calibrationRequiredHint')}
            value={draft.calibrationRequired}
            onChange={(value) => setDraft((d) => ({ ...d, calibrationRequired: value }))}
          />
          <TextAreaField
            label={t('equipment.notesLabel')}
            value={draft.notes}
            onChange={(value) => setDraft((d) => ({ ...d, notes: value }))}
            rows={2}
          />
          {save.error ? <EquipmentMutationError error={save.error} /> : null}
        </div>
      </Dialog>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Calibration history and certificates
// ---------------------------------------------------------------------------

function CalibrationHistoryPanel({
  equipment,
  mayCalibrate,
  mayUpload,
  mayRemoveCertificate,
}: {
  readonly equipment: EquipmentView;
  readonly mayCalibrate: boolean;
  readonly mayUpload: boolean;
  readonly mayRemoveCertificate: boolean;
}) {
  const { t } = useTranslation();
  const [recording, setRecording] = useState(false);

  const history = useQuery({
    queryKey: ['equipment', 'detail', equipment.id, 'calibrations'],
    queryFn: () => equipmentApi.calibrationHistory(equipment.id),
  });

  const canRecord = mayCalibrate && equipment.status !== EQUIPMENT_STATUS.RETIRED;

  return (
    <Panel
      title={t('equipment.detail.calibrationHistory')}
      description={t('equipment.detail.calibrationHistoryHint')}
      actions={
        canRecord ? (
          <ActionButton priority="secondary" size="sm" onPress={() => setRecording(true)}>
            {t('equipment.recordCalibration')}
          </ActionButton>
        ) : null
      }
    >
      {equipment.status === EQUIPMENT_STATUS.RETIRED ? (
        <div className="mb-stack">
          <Alert tone="neutral" title={t('equipment.detail.retiredNoCalibration')} />
        </div>
      ) : null}

      {history.isLoading ? (
        <LoadingState label={t('common.loading')} />
      ) : history.error ? (
        <ErrorState error={history.error} onRetry={() => void history.refetch()} />
      ) : (history.data ?? []).length === 0 ? (
        <p className="text-body text-default-600">{t('equipment.detail.noCalibrations')}</p>
      ) : (
        <ul className="flex flex-col divide-y divide-default-100" data-testid="calibration-history">
          {(history.data ?? []).map((row) => (
            <CalibrationRow
              key={row.id}
              equipmentId={equipment.id}
              row={row}
              mayUpload={mayUpload}
              mayRemoveCertificate={mayRemoveCertificate}
            />
          ))}
        </ul>
      )}

      {recording ? (
        <RecordCalibrationDialog equipment={equipment} onClose={() => setRecording(false)} />
      ) : null}
    </Panel>
  );
}

function CalibrationRow({
  equipmentId,
  row,
  mayUpload,
  mayRemoveCertificate,
}: {
  readonly equipmentId: string;
  readonly row: EquipmentCalibrationView;
  readonly mayUpload: boolean;
  readonly mayRemoveCertificate: boolean;
}) {
  const { t } = useTranslation();
  const { show } = useToast();
  const queryClient = useQueryClient();
  const locale = currentLocale();

  const [note, setNote] = useState('');
  const [removing, setRemoving] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['equipment', 'detail', equipmentId] });

  const upload = useMutation({
    mutationFn: (file: File) =>
      equipmentApi.uploadCertificate(equipmentId, row.id, {
        file,
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    onSuccess: async () => {
      await invalidate();
      setNote('');
      show({ tone: 'success', message: t('equipment.certificates.attached') });
    },
  });

  const remove = useMutation({
    mutationFn: () =>
      equipmentApi.removeCertificate(equipmentId, row.id, removing as string, reason.trim()),
    onSuccess: async () => {
      await invalidate();
      setRemoving(null);
      setReason('');
      show({ tone: 'success', message: t('equipment.certificates.removed') });
    },
  });

  return (
    <li className="flex flex-col gap-stack py-stack first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-start gap-inline">
        <div className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-inline">
            <span className="measure font-semibold text-default-900">
              {formatCalendarDate(row.effectiveOn, locale)}
            </span>
            {row.isCurrent ? (
              <StatusBadge size="sm" tone="success" label={t('equipment.detail.currentCalibration')} />
            ) : null}
          </span>
          <p className="text-caption text-default-600">
            {row.calibratedOn === null
              ? t('equipment.detail.dateOnlyEntry')
              : t('equipment.detail.calibratedOn', {
                  date: formatCalendarDate(row.calibratedOn, locale),
                })}
            {' · '}
            {row.validUntil
              ? t('equipment.validUntil', { date: formatCalendarDate(row.validUntil, locale) })
              : t('equipment.noCalibrationRecorded')}
          </p>
          {row.provider || row.certificateNumber ? (
            <p className="text-caption text-default-600">
              {row.provider ? <bdi>{row.provider}</bdi> : null}
              {row.provider && row.certificateNumber ? ' · ' : null}
              {row.certificateNumber ? (
                <span className="measure" dir="ltr">{row.certificateNumber}</span>
              ) : null}
            </p>
          ) : null}
          {row.note ? (
            <p className="text-caption text-default-600 whitespace-pre-wrap">{row.note}</p>
          ) : null}
          <p className="text-caption text-default-600">
            <bdi>{row.recordedBy.displayName ?? t('common.notRecorded')}</bdi>
            {' · '}
            <bdi>{formatDateTime(row.recordedAt, { locale })}</bdi>
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-inline">
        <p className="text-caption font-medium text-default-700">
          {t('equipment.certificates.title')}
        </p>
        {row.certificates.length === 0 ? (
          <p className="text-caption text-default-600">{t('equipment.certificates.none')}</p>
        ) : (
          <ul className="flex flex-col gap-inline">
            {row.certificates.map((certificate) => (
              <li
                key={certificate.id}
                className={`flex flex-wrap items-center gap-inline ${certificate.removedAt ? 'opacity-60' : ''}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-caption text-default-800" dir="auto">
                    {certificate.fileName}
                  </p>
                  <p className="text-caption text-default-600">
                    <bdi>{formatBytes(certificate.byteSize, { locale })}</bdi>
                    {' · '}
                    <bdi>{certificate.attachedBy.displayName ?? t('common.notRecorded')}</bdi>
                    {' · '}
                    <bdi>{formatDateTime(certificate.attachedAt, { locale })}</bdi>
                  </p>
                  {certificate.removedAt ? (
                    <p className="text-caption text-default-600">
                      {t('equipment.certificates.removedNotice', {
                        reason: certificate.removalReason ?? '',
                      })}
                    </p>
                  ) : null}
                </div>
                <FileDownloadButton
                  fileId={certificate.id}
                  fileName={certificate.fileName}
                  fetchContent={() =>
                    equipmentApi.downloadCertificate(equipmentId, row.id, certificate.id)
                  }
                />
                {mayRemoveCertificate && !certificate.removedAt ? (
                  <ActionButton
                    priority="tertiary"
                    size="sm"
                    onPress={() => setRemoving(certificate.id)}
                  >
                    {t('common.remove')}
                  </ActionButton>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {mayUpload ? (
          <div className="mt-stack flex flex-col gap-stack border-t border-default-100 pt-stack">
            <TextField
              label={t('equipment.certificates.noteLabel')}
              value={note}
              onChange={setNote}
            />
            <FileUploadField<EquipmentCalibrationView>
              label={t('equipment.certificates.attach')}
              description={t('files.notVerified')}
              upload={(file) => upload.mutateAsync(file)}
              onUploaded={() => {
                /* handled in the mutation's onSuccess */
              }}
            />
            {upload.error ? <EquipmentMutationError error={upload.error} /> : null}
          </div>
        ) : null}
      </div>

      <Dialog
        isOpen={removing !== null}
        onClose={() => setRemoving(null)}
        title={t('equipment.certificates.removeTitle')}
        footer={
          <ActionBar>
            <ActionButton priority="tertiary" onPress={() => setRemoving(null)} isDisabled={remove.isPending}>
              {t('common.cancel')}
            </ActionButton>
            <ActionButton
              priority="primary"
              isDisabled={reason.trim().length < 3}
              isLoading={remove.isPending}
              onPress={() => remove.mutate()}
            >
              {t('common.remove')}
            </ActionButton>
          </ActionBar>
        }
      >
        <div className="flex flex-col gap-stack">
          <p className="text-body text-default-700">{t('equipment.certificates.removeBody')}</p>
          <TextAreaField label={t('common.reason')} value={reason} onChange={setReason} isRequired rows={2} />
          {remove.error ? <EquipmentMutationError error={remove.error} /> : null}
        </div>
      </Dialog>
    </li>
  );
}

function RecordCalibrationDialog({
  equipment,
  onClose,
}: {
  readonly equipment: EquipmentView;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const { show } = useToast();
  const queryClient = useQueryClient();

  const [calibratedOn, setCalibratedOn] = useState<string | null>(null);
  const [validUntil, setValidUntil] = useState<string | null>(null);
  const [provider, setProvider] = useState('');
  const [certificateNumber, setCertificateNumber] = useState('');
  const [note, setNote] = useState('');
  const [showErrors, setShowErrors] = useState(false);

  const orNull = (value: string) => (value.trim() === '' ? null : value.trim());
  const orderProblem =
    calibratedOn !== null && validUntil !== null && validUntil < calibratedOn
      ? t('equipment.detail.validUntilBeforeCalibratedOn')
      : null;

  const record = useMutation({
    mutationFn: () =>
      equipmentApi.recordCalibration(equipment.id, {
        calibratedOn,
        validUntil,
        ...(orNull(provider) ? { provider: orNull(provider) } : {}),
        ...(orNull(certificateNumber) ? { certificateNumber: orNull(certificateNumber) } : {}),
        ...(orNull(note) ? { note: orNull(note) } : {}),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['equipment'] });
      show({ tone: 'success', message: t('equipment.detail.calibrationRecorded') });
      onClose();
    },
  });

  const submit = () => {
    setShowErrors(true);
    if (calibratedOn === null || orderProblem) return;
    record.mutate();
  };

  return (
    <Dialog
      isOpen
      onClose={onClose}
      title={t('equipment.recordCalibration')}
      footer={
        <ActionBar>
          <ActionButton priority="tertiary" onPress={onClose} isDisabled={record.isPending}>
            {t('common.cancel')}
          </ActionButton>
          <ActionButton
            priority="primary"
            isLoading={record.isPending}
            isDisabled={calibratedOn === null || Boolean(orderProblem)}
            onPress={submit}
          >
            {t('common.save')}
          </ActionButton>
        </ActionBar>
      }
    >
      <div className="flex flex-col gap-stack">
        <Alert tone="info" title={t('equipment.detail.calibrationNoticeTitle')} />
        <DateField
          label={t('equipment.detail.calibratedOnLabel')}
          value={calibratedOn}
          onChange={setCalibratedOn}
          isRequired
          {...(showErrors && calibratedOn === null ? { error: t('validation.required') } : {})}
        />
        <DateField
          label={t('equipment.validUntilLabel')}
          description={t('equipment.validUntilClearHint')}
          value={validUntil}
          onChange={setValidUntil}
          {...(showErrors && orderProblem ? { error: orderProblem } : {})}
        />
        <TextField
          label={t('equipment.detail.providerLabel')}
          value={provider}
          onChange={setProvider}
        />
        <TextField
          label={t('equipment.detail.certificateNumberLabel')}
          value={certificateNumber}
          onChange={setCertificateNumber}
        />
        <TextAreaField label={t('equipment.calibrationNoteLabel')} value={note} onChange={setNote} rows={2} />
        {record.error ? <EquipmentMutationError error={record.error} /> : null}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Usage history — read-only
// ---------------------------------------------------------------------------

function UsageHistoryPanel({ equipmentId }: { readonly equipmentId: string }) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const [page, setPage] = useState(1);

  const usage = useQuery({
    queryKey: ['equipment', 'detail', equipmentId, 'usage', page],
    queryFn: () => equipmentApi.usage(equipmentId, { page }),
  });

  const columns: readonly DataColumn<EquipmentUsageView>[] = [
    {
      key: 'usedOn',
      header: t('equipment.usage.usedOn'),
      render: (row) => (
        <div className="min-w-0">
          <span className="block text-default-900">{formatCalendarDate(row.usedOn, locale)}</span>
          {row.removed ? (
            <StatusBadge size="sm" tone="neutral" label={t('equipment.usage.removed')} />
          ) : null}
        </div>
      ),
    },
    {
      key: 'test',
      header: t('equipment.usage.test'),
      render: (row) => (
        <div className="min-w-0">
          <bdi className="block truncate">
            {locale === 'ar'
              ? (row.test.name.ar ?? row.test.name.en ?? t('common.notRecorded'))
              : (row.test.name.en ?? row.test.name.ar ?? t('common.notRecorded'))}
          </bdi>
          <span className="measure block text-caption text-default-600" dir="ltr">
            {row.sample.number}
          </span>
        </div>
      ),
    },
    {
      key: 'performedBy',
      header: t('equipment.usage.performedBy'),
      render: (row) => <bdi>{row.performedBy.displayName ?? t('common.notRecorded')}</bdi>,
    },
    {
      key: 'exception',
      header: t('equipment.usage.exception'),
      render: (row) =>
        row.usedByException ? (
          <StatusBadge
            size="sm"
            tone="warning"
            label={t(`equipmentBlock.${row.blockReason ?? 'suspended'}`)}
          />
        ) : (
          <span className="text-caption text-default-600">{t('common.none')}</span>
        ),
    },
  ];

  return (
    <Panel title={t('equipment.usage.title')} description={t('equipment.usage.hint')}>
      <DataTable
        label={t('equipment.usage.title')}
        columns={columns}
        rows={usage.data?.data ?? []}
        rowKey={(row) => row.id}
        isLoading={usage.isLoading}
        error={usage.error}
        onRetry={() => void usage.refetch()}
        emptyContent={t('equipment.usage.none')}
        page={page}
        totalPages={usage.data?.meta.totalPages ?? 1}
        onPageChange={setPage}
      />
    </Panel>
  );
}
